import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { PartnerSearchResult, PartnerSearchSettings, Partner, PartnerType, PartnerLoadOptions } from '../models';
import { JsonHttpService } from '../../shared/http/json-http.service';

@Injectable({
    providedIn: 'root'
})
export class PartnerService {

    public static maxNumberOfPartnersToShow = 10;

    constructor(private httpService: JsonHttpService) { }

    /**
     * Search for partners with the given search-criterias. The returned result will be an array
     * of light-partner-objects of the type PartnerSearch wrapped in a PartnerSearchResult.
     * @param searchSettings The search-criterias
     */
    searchPartners(searchSettings: PartnerSearchSettings): Promise<PartnerSearchResult> {
        const options = {
            params: new HttpParams()
                .set('searchString', searchSettings.searchString || '')
                .set('extendedSearch', searchSettings.extendedSearch + '')
                .set('includeLatesDocumentDate', searchSettings.includeLatesDocumentDate + '')
                .set('skipMaxSearchResult', searchSettings.skipMaxSearchResult + '')
                .set('includePartnerOnboarding', searchSettings.includePartnerOnboarding + '')
                .set('allPartnerOnboarding', searchSettings.allPartnerOnboarding + '')
        };
        if (searchSettings.onlyProxyPartners) {
            options.params = options.params.set('onlyProxyPartners', 'true');
        }
        return firstValueFrom(this.httpService.postTyped<PartnerSearchResult>('/api/partners/search', searchSettings.searchTags, options));
    }

    /**
    * Gets the default partner for the current user.
    */
    getDefaultPartnerForUser(): Promise<Partner> {
        return this.httpService.get('/api/partners/get-default-partner-for-user').toPromise();
    }

    /**
     * Gets a full partner object for the given partner-id.
     * @param partnerId The id for the partner to load.
     */
    getPartner(partnerId: number, loadOptions?: PartnerLoadOptions): Promise<Partner> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        options.params = this.setPartnerLoadOptionProperties(options.params, loadOptions);
        return this.httpService.get('/api/partners/get-partner', options).toPromise();
    }

    /**
     * Get full Partner objects for the given partner-id's.
     * @param partnerIds Id's for the partners to load.
     */
    getPartners(partnerIds: number[], loadOptions?: PartnerLoadOptions): Promise<Partner[]> {
        const options = { params: new HttpParams() };
        options.params = this.setPartnerLoadOptionProperties(options.params, loadOptions);
        return this.httpService.post('/api/partners/get-partners', partnerIds, options).toPromise();
    }

    /**
     * Gets a light partner object for the given partner-id. This call doesn't require the Partner_View_PartnerDetails
     * permission, and will only return absolute minimum information about the partner.
     * @param partnerId The id for the partner to load.
     */
    getLightPartner(partnerId: number): Promise<Partner> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        return this.httpService.get('/api/partners/get-light-partner', options).toPromise();
    }

    /**
     * Get light Partner objects for the given partner-id's.
     * @param partnerIds Id's for the partners to load.
     */
    getLightPartners(partnerIds: number[]): Promise<Partner[]> {
        const options = { params: new HttpParams() };
        return this.httpService.post('/api/partners/get-light-partners', partnerIds, options).toPromise();
    }

    /**
     * Gets the related partners for the given proxy partner-id.
     * @param partnerId The id for the partner to load.
     */
    getProxyPartnerConnections(proxyPartnerId: number, loadOptions?: PartnerLoadOptions): Promise<Partner[]> {
        const options = { params: new HttpParams().set('proxyPartnerId', `${proxyPartnerId}`) };
        options.params = this.setPartnerLoadOptionProperties(options.params, loadOptions);
        return this.httpService.get('/api/partners/get-proxy-partner-connections', options).toPromise();
    }

    saveProxyPartnerConnections(proxyPartnerId: number, connectedPartners: number[]): Promise<void> {
        const options = { params: new HttpParams().set('proxyPartnerId', `${proxyPartnerId}`) };
        return this.httpService.post('/api/partners/save-proxy-partner-connections', connectedPartners, options).toPromise();
    }

    setPartnerLoadOptionProperties(searchParams: HttpParams, loadOptions: PartnerLoadOptions = new PartnerLoadOptions()): HttpParams {
        Object.entries(loadOptions).forEach(([key, value]: [string, string | number | boolean]) => {
            if (value !== undefined && value !== null) {
                searchParams = searchParams.set(key, value.toString());
            }
        });
        return searchParams;
    }

    savePartner(partner: Partner): Promise<Partner> {
        return this.httpService.post('/api/partners/save-partner', partner).toPromise();
    }

    deletePartner(partnerId: number): Promise<void> {
        const options = { params: new HttpParams().set('partnerId', `${partnerId}`) };
        return this.httpService.post('/api/partners/delete-partner', null, options).toPromise();
    }

    /**
     * Get the available partner-types
     */
    getPartnerTypes(): Promise<PartnerType[]> {
        return this.httpService.get('/api/partners/get-partner-types').toPromise();
    }

    /**
     * Checks if the currently logged-in user has access to the given partners. This is only used if access-restriction is turned on.
     * @param partnerIds
     */
    checkPartnerAccess(partnerIds: number[]): Promise<boolean> {
        const options = { params: new HttpParams() };
        partnerIds.forEach(partnerId => {
            options.params = options.params.append('partnerId', `${partnerId}`);
        });
        return this.httpService.get('/api/partners/has-access-to-partners', options).toPromise();
    }

    isPartnerNameAvailable(partnerName: string): Promise<boolean> {
        const options = { params: new HttpParams().set('partnerName', `${partnerName}`) };
        return this.httpService.get('/api/partners/is-partner-name-available', options).toPromise();
    }
}
